import type { paths } from '@luxuryescapes/contract-svc-accommodation'

import request from 'api/requestUtils'
import { mapCheckoutEnquiryItem } from 'api/mappers/accommodationCheckoutEnquiryMap'
import { mapAccommodationEnquirySession } from 'api/mappers/accommodationEnquirySessionMap'
import { mapAccommodationBookingDetails } from 'api/mappers/accommodationBookingMap'

type ServerCreateEnquirySession = paths['/api/accommodation/bookings/enquiry-sessions']['post']
// NB: `brand` is supplied by `brandLoader` middleware.
type ServerCreateEnquirySessionRequestBody = Omit<
  ServerCreateEnquirySession['requestBody']['content']['application/json'],
  'brand'
>
type ServerCreateEnquirySessionResponse =
  ServerCreateEnquirySession['responses']['201']['content']['application/json']

type ServerGetBookingDetails = paths['/api/accommodation/bookings/details']['get']
type ServerGetBookingDetailsResponse =
  ServerGetBookingDetails['responses']['200']['content']['application/json']

type ServerCheckoutEnquiry = paths['/api/accommodation/bookings/enquiry']['post']
type ServerCheckoutEnquiryRequestBody =
  ServerCheckoutEnquiry['requestBody']['content']['application/json']
type ServerCheckoutEnquiryResponse =
  ServerCheckoutEnquiry['responses']['200']['content']['application/json']

type EnquirySessionBundlePayload = NonNullable<ServerCreateEnquirySessionRequestBody['bundle']>

/**
 * Create-session body. `optInBundles` is additive to the published 22.0.1 contract so a mandatory
 * `bundle` and selected optional bundles can both be persisted on the same session.
 */
export type CreateEnquirySessionPayload = ServerCreateEnquirySessionRequestBody & {
  /** Every optional bundle the customer selected, charged on top of the rate. */
  optInBundles?: Array<EnquirySessionBundlePayload>
  /**
   * Loyalty package upgrade fields, additive to 22.0.1 (published in 26.7.0). svc-accommodation
   * forwards the discount to bedbank's enquiry so its margin check runs on the price charged.
   */
  loyaltyPackageUpgradeDiscount?: number
  loyaltyUpgradeSource?: {
    serviceType: 'lpc' | 'bedbank'
    offerId?: string
    propertyId?: string
    roomId?: string
    roomRateId?: string
    packageId?: string
    sessionId?: string
  }
  createdContext?: {
    userContext?: {
      isLuxPlusUser?: boolean
      lppMemberPricingEnabled?: boolean
    }
  }
}

export type CheckoutEnquiryPayload = ServerCheckoutEnquiryRequestBody

export async function createEnquirySession(
  payload: CreateEnquirySessionPayload,
): Promise<App.AccommodationEnquirySession> {
  const response = await request.post<ServerCreateEnquirySessionResponse, CreateEnquirySessionPayload>(
    '/api/accommodation/bookings/enquiry-sessions',
    payload,
    { credentials: 'include' },
  )
  return mapAccommodationEnquirySession(response.result)
}

export async function checkoutEnquiry(
  payload: CheckoutEnquiryPayload,
): Promise<Array<App.AccommodationCheckoutEnquiryItem>> {
  const response = await request.post<ServerCheckoutEnquiryResponse, CheckoutEnquiryPayload>(
    '/api/accommodation/bookings/enquiry',
    payload,
    // brandLoader would inject `brand`; enquiry schema is objectWithOnly and rejects it.
    { credentials: 'include', excludeBrand: true },
  )
  return response.result.items.map(mapCheckoutEnquiryItem)
}

/**
 * Full booking detail for every `accommodationV2` item on an order.
 *
 * svc-accommodation composes the whole stay — guest, occupancy, property and room content, the
 * booked rate and its cancellation policy — so this is the only call needed to render a booking.
 * Supplier identity is withheld from customer callers by the service.
 */
export async function getAccommodationBookingDetails(
  orderId: string,
): Promise<Array<App.AccommodationBookingDetails>> {
  const response = await request.get<ServerGetBookingDetailsResponse>(
    `/api/accommodation/bookings/details?orderId=${encodeURIComponent(orderId)}`,
    // brandLoader would inject `brand`; the details query is objectWithOnly and rejects it.
    { credentials: 'include', excludeBrand: true },
  )
  return response.result.items.map(mapAccommodationBookingDetails)
}
